import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import StyleConstants from './styles.constants';
import DashboardTheme from '../features/statistics/dashboard/dashboard-theme';

// Shared chrome for the admin surfaces that follow the approved mockups (office detail tabs,
// linked facilities, statistics-style cards): the card shell, the section/group head, the search
// field, and the buttons and inputs those cards use.
//
// It lives in `core/` because three features render the same surface now — offices, facilities and
// parkings. Feature-specific pieces stay in the feature's own stylesheet.

export const tint = (hex: string, alpha: number): string => {
  const value = parseInt(hex.slice(1), 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
};

// Amber is too light to read as text on its own tint, so it gets a darker ink. Navy and blue are
// legible as-is.
const AMBER_INK = '#8A5A06';

export const inkFor = (accent: string): string => (accent === StyleConstants.ORANGE_COLOR ? AMBER_INK : accent);

// ---------------------------------------------------------------------------- card shell

export const card: CSSProperties = {
  background: StyleConstants.WHITE_COLOR,
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 14,
  fontFamily: DashboardTheme.bodyFontFamily,
  overflow: 'hidden',
};

// `stacked` puts the action under the heading instead of beside it — needed in narrow cards, where
// a fixed-width button beside the heading starves the description and overflows the padding.
export const cardHead = (isMobile: boolean, stacked = false): CSSProperties => ({
  display: 'flex',
  alignItems: isMobile || stacked ? 'stretch' : 'center',
  justifyContent: 'space-between',
  flexDirection: isMobile || stacked ? 'column' : 'row',
  gap: stacked ? 14 : 16,
  padding: 20,
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
});

export const cardTitle: CSSProperties = {
  fontFamily: StyleConstants.HEADER_FONT_FAMILY,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: StyleConstants.DARK_BLUE_COLOR,
  margin: 0,
};

export const cardSub: CSSProperties = {
  margin: '4px 0 0',
  fontSize: 13,
  lineHeight: 1.45,
  color: DashboardTheme.gray700,
};

export const cardMeta: CSSProperties = {
  fontSize: 11.5,
  color: DashboardTheme.gray500,
  whiteSpace: 'nowrap',
};

// ---------------------------------------------------------------------------- group head

// "Label ————————— count": one bucket of cards inside a card body.
export const groupHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 12,
};

export const groupTitle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  margin: 0,
};

export const groupRule: CSSProperties = {
  flex: 1,
  height: 1,
  background: DashboardTheme.gray200,
};

export const groupCount: CSSProperties = {
  fontSize: 11.5,
  color: DashboardTheme.gray500,
  whiteSpace: 'nowrap',
};

export const group: CSSProperties = {
  marginBottom: 28,
};

// ---------------------------------------------------------------------------- controls

// Same blue as PrimaryButton (StyleConstants.MEDIUM_BLUE_COLOR) so these don't introduce a second
// primary colour next to the app's existing buttons.
export const primaryButton: NestedCSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: `1px solid ${StyleConstants.MEDIUM_BLUE_COLOR}`,
  background: StyleConstants.MEDIUM_BLUE_COLOR,
  color: StyleConstants.WHITE_COLOR,
  borderRadius: 10,
  padding: '9px 16px',
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flex: '0 0 auto',
  transitionDuration: '200ms',
  transitionProperty: 'background-color',
  $nest: {
    '&:hover': {
      background: StyleConstants.DARK_BLUE_COLOR,
      borderColor: StyleConstants.DARK_BLUE_COLOR,
    },
    // A disabled button must both look inert and stop reacting to hover.
    '&:disabled': {
      opacity: 0.45,
      cursor: 'default',
    },
    '&:disabled:hover': {
      background: StyleConstants.MEDIUM_BLUE_COLOR,
      borderColor: StyleConstants.MEDIUM_BLUE_COLOR,
    },
  },
};

export const ghostButton: NestedCSSProperties = {
  border: `1px solid ${DashboardTheme.gray300}`,
  background: StyleConstants.WHITE_COLOR,
  color: DashboardTheme.gray700,
  borderRadius: 10,
  padding: '9px 14px',
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  $nest: {
    '&:hover': {
      background: DashboardTheme.gray50,
    },
  },
};

export const textInput: NestedCSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: '9px 12px',
  border: `1px solid ${DashboardTheme.gray300}`,
  borderRadius: 10,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13.5,
  color: DashboardTheme.navy800,
  background: StyleConstants.WHITE_COLOR,
  $nest: {
    '&:focus': {
      outline: `2px solid ${StyleConstants.MEDIUM_BLUE_COLOR}`,
      outlineOffset: -1,
      borderColor: StyleConstants.MEDIUM_BLUE_COLOR,
    },
    '&:disabled': {
      background: DashboardTheme.gray50,
      color: DashboardTheme.gray500,
    },
  },
};

// Inline field error, shown under the input that caused it.
export const fieldError: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: StyleConstants.PINK_COLOR,
  fontFamily: DashboardTheme.bodyFontFamily,
};

// PrimaryButton hardcodes a 180px width unless `fullWidth` is passed; this wrapper gives inline
// save buttons a narrower, consistent footprint.
export const inlineSaveWrap: CSSProperties = {
  width: 140,
  flexShrink: 0,
};

// Circular prev/next control for the table footers and list pagers.
export const pageButton = (disabled: boolean): NestedCSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 0,
  background: 'none',
  borderRadius: '50%',
  padding: 6,
  lineHeight: 0,
  color: DashboardTheme.gray500,
  opacity: disabled ? 0.3 : 1,
  cursor: disabled ? 'default' : 'pointer',
  $nest: {
    '&:hover': {
      background: disabled ? 'none' : DashboardTheme.gray100,
    },
  },
});

// ---------------------------------------------------------------------------- search

export const toolbar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
  padding: '20px 20px 16px',
};

export const searchWrap = (isMobile: boolean): CSSProperties => ({
  position: 'relative',
  width: isMobile ? '100%' : 280,
  maxWidth: '100%',
  flex: '0 0 auto',
});

export const searchIcon: CSSProperties = {
  position: 'absolute',
  left: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  color: DashboardTheme.gray500,
  lineHeight: 0,
  pointerEvents: 'none',
};

export const searchInput: NestedCSSProperties = {
  ...textInput,
  width: '100%',
  flex: undefined,
  paddingLeft: 36,
  boxSizing: 'border-box',
};

// ---------------------------------------------------------------------------- empty state

export const emptyState: CSSProperties = {
  textAlign: 'center',
  padding: '40px 0',
  color: DashboardTheme.gray500,
};

export const emptyTitle: CSSProperties = {
  display: 'block',
  color: StyleConstants.DARK_BLUE_COLOR,
  fontSize: 13.5,
  fontWeight: 600,
  marginTop: 8,
};

export const emptyHint: CSSProperties = {
  fontSize: 13,
};
